import { theme } from '@config/theme';
import {
	Autocomplete,
	AutocompleteRenderGroupParams,
	Avatar,
	FormControl,
	InputAdornment,
	TextField,
} from '@mui/material';
import { SxProps, Theme } from '@mui/system';
import React from 'react';

const flagCDN = 'https://flagcdn.com/w20/';

interface ReusableAutocompleteProps<T> {
	options: T[];
	getOptionLabel: (option: T) => string;
	placeholder?: string;
	multiple?: boolean;
	defaultValue?: T | T[];
	hasFlag?: boolean;
	icon?: React.ReactNode;
	label?: string;
	disabled?: boolean;
	value: T | T[] | null;
	onChange?: (event: React.SyntheticEvent, value: T | T[] | null) => void; // Allow null
	sx?: SxProps<Theme>;
	error?: boolean;
	disableCloseOnSelect?: boolean;
	required?: boolean;
	renderOption?: (props: React.HTMLAttributes<HTMLLIElement>, option: T) => React.ReactNode;
	groupBy?: (option: T) => string;
	renderGroup?: (params: AutocompleteRenderGroupParams) => React.ReactNode;
	filterSelectedOptions?: boolean;
	limitTags?: number;
	getLimitTagsText?: (more: number) => React.ReactNode;
}

export function TaAutocomplete<T>({
	options,
	getOptionLabel,
	placeholder = '',
	multiple = false,
	defaultValue,
	onChange,
	sx,
	hasFlag = false,
	label,
	icon,
	value,
	error,
	required = false,
	renderOption,
	groupBy,
	renderGroup,
	disableCloseOnSelect = false,
	filterSelectedOptions = false,
	limitTags,
	getLimitTagsText,
	disabled,
}: ReusableAutocompleteProps<T>) {
	const customRenderOptions = (props: any, option: any) => {
		if (hasFlag) {
			return (
				<li {...props}>
					<div className="flex items-center gap-2">
						<Avatar
							src={`${flagCDN}${option?.alpha_two.toLowerCase()}.png`}
							sx={{ width: 16, height: 12 }}
							variant="square"
						/>
						<span>{option?.name}</span>
					</div>
				</li>
			);
		} else if (renderOption) {
			return renderOption(props, option);
		} else {
			return (
				<li {...props}>
					<div className="flex items-center gap-2">
						<span>{option?.name}</span>
					</div>
				</li>
			);
		}
	};

	return (
		<FormControl sx={{ ...sx, width: '100%' }}>
			<Autocomplete
				disabled={disabled}
				disableCloseOnSelect={multiple || disableCloseOnSelect}
				multiple={multiple}
				disableClearable={!multiple}
				getLimitTagsText={getLimitTagsText}
				limitTags={limitTags}
				value={value}
				options={options}
				getOptionLabel={getOptionLabel}
				defaultValue={defaultValue as any}
				filterSelectedOptions={filterSelectedOptions}
				onChange={(event, value) => {
					if (onChange) onChange(event, value);
				}}
				renderOption={(props, option) => customRenderOptions(props, option)}
				groupBy={groupBy}
				renderGroup={renderGroup}
				size="small"
				sx={{
					'& .MuiOutlinedInput-root': {
						borderRadius: '8px',
					},
					'& .MuiIcon-root': { mt: -2 },
					'& .MuiOutlinedInput-notchedOutline': {
						borderWidth: error ? '2px' : '1px', // Ensure error state applies the border width
						borderColor: error
							? `${theme.palette.custom.brightRed}!important`
							: theme.palette.custom.gray,
						color: theme.palette.custom.brightRed,
					},
					'& .MuiAutocomplete-groupLabel': {
						backgroundColor: theme.palette.grey[100],
						fontWeight: 'bold',
					},
					'& .MuiAutocomplete-tag': {
						paddingLeft: '8px',
					},
				}}
				renderInput={(params) => (
					<TextField
						{...params}
						label={
							required ? (
								<>
									{label} <span style={{ color: 'red' }}>*</span>
								</>
							) : (
								label
							)
						}
						error={Boolean(error)}
						placeholder={placeholder}
						InputProps={{
							...params.InputProps,
							...(icon && {
								startAdornment: (
									<>
										<InputAdornment position="start">{icon}</InputAdornment>
										{params.InputProps.startAdornment}
									</>
								),
							}),
						}}
					/>
				)}
			/>
		</FormControl>
	);
}
